import { ProgressionDistance, ProgressionEffort, RecordDistance } from '../api/types';

/** Yearly best (dots per year plus a line through the yearly bests) or the all-time PB staircase. */
export type ProgressView = 'yearly' | 'pb';
/** Pace per km for the all-distances chart, finishing time for a single distance. */
export type ProgressMetric = 'pace' | 'time';
export type YearRange = 'all' | 'last5' | 'last2';

export const TOP_OPTIONS = [1, 3, 5] as const;
/** The API is asked for the largest N once; smaller N is a prefix of each year's ranking. */
export const API_TOP = 10;

export const RANGE_OPTIONS: Array<{ value: YearRange; label: string }> = [
  { value: 'all', label: 'All years' },
  { value: 'last5', label: 'Last 5 years' },
  { value: 'last2', label: 'Last 2 years' },
];

/** Series colors come from the theme tokens --series-1..8 (index.css). Past eight, a series is dashed too. */
const SERIES_TOKENS = [1, 2, 3, 4, 5, 6, 7, 8].map(n => `var(--series-${n})`);

export interface SeriesStyle {
  color: string;
  dashed: boolean;
}

/** Color follows the catalog position, so hiding one distance never repaints the others. */
export function seriesStyle(index: number): SeriesStyle {
  return { color: SERIES_TOKENS[index % SERIES_TOKENS.length], dashed: index >= SERIES_TOKENS.length };
}

export interface ChartPoint {
  /** Days since 1970-01-01 (the time axis). */
  x: number;
  /** Seconds per km or seconds, depending on the metric. */
  y: number;
  effort: ProgressionEffort;
  code: RecordDistance;
  name: string;
  style: SeriesStyle;
}

export interface DistanceSeries {
  code: RecordDistance;
  name: string;
  style: SeriesStyle;
  /** Small markers: the top N of each year, or every PB in the PB view. */
  dots: ChartPoint[];
  /** The yearly-best line, or the step line through the PB sequence. */
  line: ChartPoint[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Days since 1970-01-01 for a YYYY-MM-DD date. */
export function dayNumber(date: string): number {
  const [year, month, day] = date.split('-').map(Number);
  return Math.round(Date.UTC(year, month - 1, day) / DAY_MS);
}

/** Axis label for a day number, e.g. "Mar 2025". */
export function dayLabel(day: number): string {
  return new Date(day * DAY_MS).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** The first year shown for a range, or null for all years. */
export function minYearFor(range: YearRange, currentYear: number): number | null {
  if (range === 'last5') return currentYear - 4;
  if (range === 'last2') return currentYear - 1;
  return null;
}

export function yValue(effort: ProgressionEffort, metric: ProgressMetric): number {
  return metric === 'pace' ? effort.paceSPerKm : effort.timeS;
}

export interface BuildOptions {
  view: ProgressView;
  top: number;
  metric: ProgressMetric;
  minYear: number | null;
}

/**
 * Series for the chart, one per distance that has any effort in the range. The style index is the distance's position
 * in the full list (not in the filtered one), so colors stay with their distance.
 */
export function buildSeries(distances: ProgressionDistance[], options: BuildOptions): DistanceSeries[] {
  const out: DistanceSeries[] = [];
  distances.forEach((distance, index) => {
    const style = seriesStyle(index);
    const point = (effort: ProgressionEffort): ChartPoint => ({
      x: dayNumber(effort.date),
      y: yValue(effort, options.metric),
      effort,
      code: distance.distance,
      name: distance.name,
      style,
    });
    const inRange = (year: number) => options.minYear === null || year >= options.minYear;

    let dots: ChartPoint[];
    let line: ChartPoint[];
    if (options.view === 'yearly') {
      const years = distance.years.filter(y => inRange(y.year));
      dots = years.flatMap(y => y.efforts.slice(0, options.top)).map(point);
      line = years.map(y => point(y.efforts[0])).filter((p): p is ChartPoint => p !== undefined);
    } else {
      const pbs = distance.allTimeProgression.filter(e => inRange(Number(e.date.slice(0, 4)))).map(point);
      dots = pbs;
      line = pbs;
    }
    if (dots.length > 0) {
      out.push({ code: distance.distance, name: distance.name, style, dots, line });
    }
  });
  return out;
}

/** Every distance that has at least one effort in any year (the legend and the single-distance list). */
export function distancesWithEfforts(distances: ProgressionDistance[]): ProgressionDistance[] {
  return distances.filter(d => d.years.length > 0);
}

/** Axis bounds for the visible series, with a little room around the points. */
export function domainOf(series: DistanceSeries[]): { x: [number, number]; y: [number, number] } | null {
  const points = series.flatMap(s => s.dots);
  if (points.length === 0) return null;
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const pad = Math.max(5, (Math.max(...ys) - Math.min(...ys)) * 0.1);
  // A single day would give an empty axis: show half a year either side.
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const span = maxX === minX ? 182 : 0;
  return {
    x: [minX - span, maxX + span],
    y: [Math.floor(Math.min(...ys) - pad), Math.ceil(Math.max(...ys) + pad)],
  };
}
